import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useSEO, { SITE_URL } from '@/hooks/useSEO';

export default function ContactPage() {
  const { t, i18n } = useTranslation('common');
  const currentLang = i18n.language || 'en';
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useSEO({
    title: t('metaContactTitle'),
    description: t('metaContactDesc'),
    canonical: `${SITE_URL}/${currentLang}/contact`,
    lang: currentLang,
    ogType: 'website',
    ogTitle: t('metaContactTitle'),
    ogDesc: t('metaContactDesc'),
    ogUrl: `${SITE_URL}/${currentLang}/contact`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact AllChinaBuy Spreadsheet',
      url: `${SITE_URL}/${currentLang}/contact`,
      description: t('metaContactDesc'),
    },
  });

  useEffect(() => {
    document.documentElement.lang = currentLang;
  }, [currentLang]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honey = (formData.get('contact_alt') as string || '').trim();

    if (honey) {
      setStatus('success');
      return;
    }

    const email = (formData.get('email') as string || '').trim();
    const message = (formData.get('message') as string || '').trim();

    if (!email || !message) {
      setErrorMsg(t('fillRequired'));
      setStatus('error');
      return;
    }

    if (message.length > 500) {
      setErrorMsg(t('messageTooLong'));
      setStatus('error');
      return;
    }

    setStatus('success');
    setErrorMsg('');
    form.reset();
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        <nav aria-label="Breadcrumb" className="border-b border-background-200">
          <div className="container-main py-3">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to={`/${currentLang}`} className="text-foreground-500 hover:text-primary-600 transition-colors">
                  {t('breadcrumbHome')}
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-foreground-300 text-xs" /></li>
              <li className="text-foreground-700 font-medium">{t('breadcrumbContact')}</li>
            </ol>
          </div>
        </nav>

        <section className="section-padding">
          <div className="container-main max-w-2xl mx-auto">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              {t('contactTitle')}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
              {t('contactDesc')}
            </p>

            <div className="card p-6 md:p-8">
              <form onSubmit={handleSubmit} data-readdy-form="contact" noValidate>
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <div className="mb-5">
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    {t('email')} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-md border border-background-200 text-sm bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all"
                    placeholder="your@email.com"
                  />
                </div>

                <div className="mb-5">
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-3 rounded-md border border-background-200 text-sm bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all"
                    placeholder="How can we help?"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Message * (max 500 characters)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-md border border-background-200 text-sm bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all resize-none"
                    placeholder="Tell us more about your question or suggestion..."
                  />
                  <p className="text-xs text-foreground-400 mt-1">Maximum 500 characters</p>
                </div>

                {status === 'error' && errorMsg && (
                  <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                    {errorMsg}
                  </div>
                )}

                {status === 'success' && (
                  <div className="mb-4 p-3 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-800">
                    {t('thankYouMessage')}
                  </div>
                )}

                <button type="submit" className="btn-primary w-full gap-2">
                  <i className="ri-send-plane-line" />
                  {t('sendMessage')}
                </button>
              </form>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="card p-5 text-center">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-primary-600 text-lg" />
                </div>
                <h3 className="font-semibold text-sm text-foreground-900 mb-1">{t('email')}</h3>
                <p className="text-xs text-foreground-500">contact@allchinabuyspread.hair</p>
              </div>
              <div className="card p-5 text-center">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-accent-600 text-lg" />
                </div>
                <h3 className="font-semibold text-sm text-foreground-900 mb-1">{t('responseTime')}</h3>
                <p className="text-xs text-foreground-500">Within 24-48 hours</p>
              </div>
              <div className="card p-5 text-center">
                <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-global-line text-secondary-600 text-lg" />
                </div>
                <h3 className="font-semibold text-sm text-foreground-900 mb-1">{t('website')}</h3>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-xs text-foreground-500 hover:text-primary-600"
                >
                  kaspreadsheet.com
                </a>
              </div>
            </div>

            <div className="mt-8 p-4 rounded-lg bg-primary-50 border border-primary-200">
              <p className="text-sm text-foreground-700">
                Return to <Link to={`/${currentLang}`} className="text-primary-600 hover:text-primary-700 underline">homepage</Link> or browse our <Link to={`/${currentLang}/blog`} className="text-primary-600 hover:text-primary-700 underline">latest articles</Link>.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}